import {toast} from "sonner";
import api from "./api";
import {useQuery} from "@tanstack/react-query";
import {useRef} from "react";
import {IProgressReport} from "@/types/report.ts";
import {trackReportShared} from "@/lib/posthog.ts";
import type { AnalyticsSource } from "@/types/analytics";

export interface ProgressDataItem {
  /**
   * Format: mm-yy (e.g. "08-25")
   */
  date: string;
  /**
   * Either 0-1 (string) or 0-100 (string/number). UI will normalize.
   */
  score: string | number;
}

export interface ScheduleConfig {
  id: number;
  company_id: number;
  company_name: string;
  recipients: string[];
  is_enabled: boolean;
  next_generation_ts: string;
}

export class ProjectsService {
  private readonly baseUrl = `${import.meta.env.VITE_API_URL}/api`;

  private getAuthHeader(token: string | null) {
    return { Authorization: `JWT ${token}` };
  }

  async getCompanies(token: string | null): Promise<
    {
      company_id: number;
      company_name: string;
      lastReportId: number;
      status: "in_progress" | "completed" | "error";
    }[]
  > {
    const res = await fetch(`${this.baseUrl}/user-companies/`, {
      headers: this.getAuthHeader(token),
    });

    if (!res.ok) throw new Error(`Failed to load companies (${res.status})`);

    const data = await res.json();
    
    // The backend now returns exactly one entry per company (the latest one)
    // with fields: company_id, company_name, lastReportId, status, created_ts
    
    return data.map((item: any) => ({
      company_id: item.company_id,
      company_name: item.company_name,
      lastReportId: item.lastReportId,
      status: (["in_progress", "completed", "error"].includes(item.status)
        ? item.status
        : "error") as "in_progress" | "completed" | "error",
    }));
  }

  async getReports(
    token: string | null,
    companyId?: number | string,
  ): Promise<{ results: IProgressReport[] }> {
    const response = await api.get<{ results: IProgressReport[] }>("/reports", {
      headers: this.getAuthHeader(token),
      params: companyId ? { company_id: companyId } : undefined,
    });
    return response.data;
  }

  async getReportById(id: number, token: string | null): Promise<any> {
    const response = await api.get(`/reports/${id}/`, {
      headers: this.getAuthHeader(token),
    });
    const data = response.data;
    if (!data.signals) {
      return {
        id: data.id,
        company_id: data.company_id,
        company_name: data.company_name,
        company_url: data.company_url,
        analytics: {
          totalScore: `${Math.round(parseFloat(data.overall_score) * 100)}`,
          trustSignals: 0,
        },
        signals: {},
        signalsList: [],
        status: data.status,
      };
    }

    const { signals: rawSignals } = data;

    if (!rawSignals || !Array.isArray(rawSignals)) {
      throw new Error("Invalid report data format");
    }

    const signals = rawSignals.filter((s) => s.definition?.name);

    const trustSignals = signals.filter((s) => s.to_improve).length;

    const grouped = signals.reduce(
      (acc: any, sig: any) => {
        const { definition, score, to_improve, technical_signals = [] } = sig;

        const entry = {
          ...definition,
          ...sig,
          score,
          to_improve,
          technical_signals,
        };

        const cat = definition.category || "uncategorized";
        acc[cat] = acc[cat] || [];
        acc[cat].push(entry);
        return acc;
      },
      {} as Record<string, any[]>,
    );

    return {
      ...data,
      analytics: {
        totalScore: `${Math.round(parseFloat(data.overall_score) * 100)}`,
        trustSignals,
      },
      signals: grouped,
      signalsList: signals,
    };
  }

  async shareReport(
    reportId: number,
    recipients: string[],
    token: string | null,
    workspaceId?: number | null,
    companyId?: number | null,
    companyName?: string | null,
  ): Promise<void> {
    const res = await fetch(`${this.baseUrl}/send-report-email/`, {
      method: "POST",
      headers: {
        ...this.getAuthHeader(token),
        "Content-Type": "application/json",
        ...(workspaceId ? { "X-Workspace-ID": String(workspaceId) } : {}),
      },
      body: JSON.stringify({
        report_id: reportId,
        recipients_list: recipients,
      }),
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Failed to share report (${res.status}): ${text}`);
    }
    trackReportShared({
      report_id: reportId,
      share_method: "email",
      recipient_count: recipients.length,
      company_id: companyId,
      company_name: companyName,
    })
  }

  async createReport(
    companyName: string,
    companyUrl: string,
    recurringMonthly: boolean,
    token: string | null,
    workspaceId?: number | null,
    source?: AnalyticsSource,
  ): Promise<{
    success: boolean;
    reportId?: number;
    companyId?: number;
    message?: string;
    code?: string;
    onboardingRequired?: boolean;
    resumeStep?: string;
    resumeKey?: string;
  }> {
    try {
      const res = await fetch(
        `${this.baseUrl}/reports/create/`,
        {
          method: "POST",
          headers: {
            ...this.getAuthHeader(token),
            "Content-Type": "application/json",
            ...(workspaceId ? { "X-Workspace-ID": String(workspaceId) } : {}),
          },
          body: JSON.stringify({
            company_name: companyName,
            company_url: companyUrl,
            recurring_monthly: recurringMonthly,
            ...(workspaceId ? { workspace_id: workspaceId } : {}),
            ...(source ? { source } : {}),
          }),
        },
      );

      if (!res.ok) {
        let errorMessage = `Failed to load reports (${res.status})`;
        let reportId: number | null = null;
        let companyId: number | null = null;
        let code: string | undefined;
        let onboardingRequired: boolean | undefined;
        let resumeStep: string | undefined;
        let resumeKey: string | undefined;

        try {
          const errData = await res.json();

          if (errData?.detail) {
            errorMessage = errData.detail;
          }

          if (errData?.report_id) {
            reportId = errData.report_id;
          }

          if (errData?.company_id) {
            companyId = errData.company_id;
          }
          if (typeof errData?.code === "string") {
            code = errData.code;
          }
          if (typeof errData?.onboarding_required === "boolean") {
            onboardingRequired = errData.onboarding_required;
          }
          if (typeof errData?.resume_step === "string") {
            resumeStep = errData.resume_step;
          }
          if (typeof errData?.resume_key === "string") {
            resumeKey = errData.resume_key;
          }
        } catch {}

        // You could throw a richer error object with both values:
        const err: any = new Error(errorMessage);
        if (reportId) err.reportId = reportId;
        if (companyId) err.companyId = companyId;
        if (code) err.code = code;
        if (onboardingRequired !== undefined) err.onboardingRequired = onboardingRequired;
        if (resumeStep) err.resumeStep = resumeStep;
        if (resumeKey) err.resumeKey = resumeKey;
        throw err;
      }

      try {
        const responseData = await res.json();
        return {
          success: true,
          reportId: responseData.report?.report_id || responseData.report_id || responseData.id,
          companyId: responseData.report?.company_id ?? responseData.company_id,
        };
      } catch {
        return { success: true };
      }
    } catch (err: any) {
      console.error("Error creating report", err);

      const isOnboardingRequired =
        err?.code === "billing_onboarding_required" ||
        err?.onboardingRequired === true;

      // you can check for reportId here if you want
      if (!err?.reportId && !isOnboardingRequired) {
        toast.error("Failed to create report");
      }

      return {
        success: false,
        reportId: err?.reportId,
        companyId: err?.companyId,
        message: err?.message,
        code: err?.code,
        onboardingRequired: err?.onboardingRequired,
        resumeStep: err?.resumeStep,
        resumeKey: err?.resumeKey,
      };
    }
  }

  async createScheduledReport(
    companies: number[],
    recipients: string[],
    generationDay: number,
    token: string | null,
  ): Promise<void> {
    try {
      // Backend expects one schedule per company, so create multiple if needed
      for (const companyId of companies) {
        await api.post(
          "/report-schedules/",
          {
            company_id: companyId,
            recipients: recipients,
            is_enabled: true,
            generation_day: generationDay,
          },
          {
            headers: this.getAuthHeader(token),
          },
        );
      }

      toast.success("Scheduled report created successfully");
    } catch (err) {
      console.error("Error scheduling report", err);
      toast.error("Failed to schedule report");
    }
  }

  async upgradeReport(
    reportId: number,
    token: string | null,
    workspaceId?: number | null,
  ): Promise<{ report?: { detail?: string; report_id?: number } }> {
    try {
      const res = await fetch(`${this.baseUrl}/reports/upgrade/`, {
        method: "POST",
        headers: {
          ...this.getAuthHeader(token),
          "Content-Type": "application/json",
          ...(workspaceId ? { "X-Workspace-ID": String(workspaceId) } : {}),
        },
        body: JSON.stringify({ report_id: reportId }),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to upgrade report (${res.status}): ${text}`);
      }

      let data: { report?: { detail?: string; report_id?: number } } = {};
      try {
        data = await res.json();
      } catch {
        // ignore json parse error; backend might return empty body
      }
      toast.success("Report upgrade started");
      return data;
    } catch (err) {
      console.error("Error upgrading report", err);
      toast.error("Failed to run full report");
      return {};
    }
  }

  async getScheduledReports(token: string | null, workspaceId?: number): Promise<ScheduleConfig[]> {
    try {
      const response = await api.get("/report-schedules/", {
        headers: {
          ...this.getAuthHeader(token),
          ...(workspaceId ? { "X-Workspace-ID": String(workspaceId) } : {}),
        },
      });

      const data = response.data;
      const schedules = data.results || [];
      
      // Sort: active schedules first, then inactive
      return schedules.sort((a: ScheduleConfig, b: ScheduleConfig) => {
        if (a.is_enabled === b.is_enabled) return 0;
        return a.is_enabled ? -1 : 1;
      });
    } catch (error) {
      console.error("Error fetching scheduled reports:", error);
      throw error;
    }
  }

  async updateScheduledReport(
    id: number,
    updates: {
      company_id?: number,
      recipients?: string[],
      generation_day?: number,
      is_enabled?: boolean
    },
    successMessage?: string
  ) {
    try {
      const response = await api.patch(`/report-schedules/${id}/`, updates);

      if (!response.data) {
        throw new Error("Failed to update scheduled report");
      }

      toast.success(successMessage || "Scheduled report updated successfully");
    } catch (err) {
      console.error("Error updating scheduled report", err);
      toast.error("Failed to update scheduled report");
    }
  }

  async deleteScheduledReport(id: number) {
    try {
      const response = await api.delete(`/report-schedules/${id}/`);

      if (response.status !== 204) {
        throw new Error("Failed to delete scheduled report");
      }

      toast.success("Scheduled report deleted successfully");
    } catch (err) {
      console.error("Error deleting scheduled report", err);
      toast.error("Failed to delete scheduled report");
    }
  }

  async getProgressData(
    companyId: number,
    token: string | null,
    params?: { start_date?: string; end_date?: string },
  ): Promise<ProgressDataItem[]> {
    const search = new URLSearchParams();
    if (params?.start_date) search.set("start_date", params.start_date);
    if (params?.end_date) search.set("end_date", params.end_date);

    const qs = search.toString();
    const url = `${this.baseUrl}/companies/${companyId}/progress${qs ? `?${qs}` : ""}`;

    const res = await fetch(url, {
      headers: this.getAuthHeader(token),
    });

    if (!res.ok) {
      throw new Error(`Failed to load progress data (${res.status})`);
    }

    const json = await res.json();
    // Backend may return an array or { results: [] }.
    if (Array.isArray(json)) return json;
    if (Array.isArray(json?.results)) return json.results;
    return [];
  }

  async reRunReport(
    reportId: number,
    token: string | null,
  ): Promise<{ success: boolean; reportId?: number; errorCode?: number }> {
    try {
      const res = await fetch(`${this.baseUrl}/reports/${reportId}/re-run/`, {
        method: "POST",
        headers: {
          ...this.getAuthHeader(token),
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        return { success: false, errorCode: res.status };
      }

      const data = await res.json();
      return {
        success: true,
        reportId: data.report?.report_id,
      };
    } catch (err) {
      console.error("Error rerunning report", err);
      return { success: false };
    }
  }

  async updateReportName(
    reportId: number,
    newName: string,
    token: string | null
  ): Promise<void> {
    try {
      const res = await fetch(`${this.baseUrl}/reports/update-name/`, {
        method: "PATCH",
        headers: {
          ...this.getAuthHeader(token),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          report_id: reportId,
          report_name: newName,
        }),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to update report name (${res.status}): ${text}`);
      }

      toast.success("Report name updated successfully");
    } catch (err) {
      console.error("Error updating report name", err);
      toast.error("Failed to update report name");
      throw err;
    }
  }
}

export const projectsInstance = new ProjectsService();

export const useGetScheduledReports = (
  token: string | null,
  enabled: boolean = true,
  workspaceId?: number | null,
) => {
  const tokenRef = useRef(token);
  tokenRef.current = token;
  return useQuery<ScheduleConfig[]>({
    queryKey: ["scheduledReports", workspaceId ?? null],
    queryFn: async () =>
      projectsInstance.getScheduledReports(tokenRef.current, workspaceId ?? undefined),
    enabled: !!token && enabled,
  });
};

export const useGetProgressData = (args: {
  token: string | null;
  companyId: number | null | undefined;
  startDate?: string;
  endDate?: string;
  enabled?: boolean;
}) => {
  const { token, companyId, startDate, endDate, enabled = true } = args;

  return useQuery<ProgressDataItem[]>({
    queryKey: ["progressData", companyId, startDate ?? null, endDate ?? null],
    queryFn: async () => {
      if (!companyId) return [];
      return await projectsInstance.getProgressData(companyId, token, {
        start_date: startDate,
        end_date: endDate,
      });
    },
    enabled: !!token && !!companyId && enabled,
    staleTime: 1000 * 60 * 10, // 10 min
  });
};
